import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "../../styles/pages/games/NeonMemory.module.scss";

const EMOJI = [
  "🌟","💎","🚀","⚡","🎯","🧠","🛰️","🧩","🧪","🔬","📐","📚","🧮","🎵","🎨","🪐",
  "🔭","🧱","🧷","🧵","🔗","♻️","💡","🎲","📎","🚦","✏️","📈","🧭","🪄","🖱️","⌨️",
  "🧲","🧰","🧫","📡","🧯","🧊","🧬","🧿","🔋","🧪","🧷","🧠"
];

const LEVELS = {
  "4x4": { cols: 4, rows: 4, timeLimit: 60, label: "Rookie" },
  "5x4": { cols: 5, rows: 4, timeLimit: 90, label: "Pro" },
};

function shuffled(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = (Math.random() * (i + 1)) | 0;
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function makeDeck(cfg) {
  const total = cfg.cols * cfg.rows;
  const safeTotal = total % 2 === 0 ? total : total - 1;
  const pairs = safeTotal / 2;

  const pick = shuffled(EMOJI).slice(0, pairs);
  const deck = shuffled([...pick, ...pick]).map((v, i) => ({
    id: i,
    v,
    flipped: false,
    matched: false,
  }));
  return { deck, pairs, total: safeTotal };
}

function fmtLevel(key, cfg) {
  return `${cfg.label} (${key.replace("x", "×")})`;
}

/* -------- WebAudio SFX (same as before, minimal) -------- */
function createSfx() {
  let ctx = null;
  let master = null;
  const state = { on: true };

  const ensure = async () => {
    if (!ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctx = new AC();
      master = ctx.createGain();
      master.gain.value = 0.14;
      master.connect(ctx.destination);
    }
    if (ctx.state === "suspended") await ctx.resume();
  };

  const tone = async (f, d = 0.08, type = "triangle", gain = 0.8) => {
    if (!state.on) return;
    await ensure();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.value = f;

    const t = ctx.currentTime;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(0.12 * gain, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + d);

    o.connect(g);
    g.connect(master);

    o.start(t);
    o.stop(t + d + 0.02);
  };

  const noise = async (d = 0.05, gain = 0.22) => {
    if (!state.on) return;
    await ensure();
    const bufferSize = Math.max(1, (ctx.sampleRate * d) | 0);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * 0.7;

    const src = ctx.createBufferSource();
    src.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = "highpass";
    filter.frequency.value = 900;

    const g = ctx.createGain();
    g.gain.value = gain * 0.12;

    src.connect(filter);
    filter.connect(g);
    g.connect(master);

    src.start();
    src.stop(ctx.currentTime + d + 0.02);
  };

  return {
    unlock: ensure,
    setOn: (v) => (state.on = v),
    getOn: () => state.on,

    ui: () => tone(720, 0.06, "triangle", 0.7),
    flip: () => tone(520 + Math.random() * 70, 0.05, "triangle", 0.55),
    match: async () => { await tone(660, 0.08, "triangle", 0.95); await tone(880, 0.08, "triangle", 0.9); },
    miss: async () => { await tone(210, 0.12, "square", 0.85); await noise(0.05, 0.35); },
    win: async () => { for (const f of [660, 880, 990, 1320]) await tone(f, 0.09, "sawtooth", 0.9); },
    lose: async () => { await tone(220, 0.18, "square", 0.95); await tone(160, 0.20, "square", 0.85); },
  };
}

export default function NeonMemory({ size = "4x4", onExit }) {
  // Level
  const [levelKey, setLevelKey] = useState(size);
  const cfg = useMemo(() => LEVELS[levelKey] || LEVELS["4x4"], [levelKey]);

  const board = useMemo(() => makeDeck(cfg), [cfg]);
  const pairs = board.pairs;

  // Mode + timers
  const [timed, setTimed] = useState(true);
  const [running, setRunning] = useState(true);

  // State
  const [deck, setDeck] = useState(() => board.deck);
  const [first, setFirst] = useState(null);
  const [second, setSecond] = useState(null);

  const [moves, setMoves] = useState(0);
  const [matches, setMatches] = useState(0);
  const [combo, setCombo] = useState(0);

  const [toast, setToast] = useState("");
  const [shake, setShake] = useState(false);
  const [ghost, setGhost] = useState({ map: {}, until: 0 });

  const initialTime = timed ? cfg.timeLimit : 0;
  const [time, setTime] = useState(initialTime);

  const [result, setResult] = useState(null); // {type,title,subtitle}

  // Best (timed: lower is better)
  const bestKey = useMemo(() => `mm_best_${levelKey}`, [levelKey]);
  const [best, setBest] = useState(() => Number(localStorage.getItem(bestKey) || 0));

  // Locks
  const lockRef = useRef(false);

  // Audio
  const sfxRef = useRef(null);
  if (!sfxRef.current) sfxRef.current = createSfx();
  const [soundOn, setSoundOn] = useState(true);

  useEffect(() => {
    sfxRef.current.setOn(soundOn);
  }, [soundOn]);

  // ---- Responsive grid sizing (kept) ----
  const stageRef = useRef(null);
  const [layout, setLayout] = useState({ card: 110, gap: 14 });

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;

    const ro = new ResizeObserver(() => {
      const rect = el.getBoundingClientRect();

      // leave space for toast + result overlay on small screens
      const pad = 20;
      const reserveForOverlay = 160; // safe buffer so grid never hides behind result
      const availW = Math.max(280, rect.width - pad * 2);
      const availH = Math.max(280, rect.height - pad * 2 - reserveForOverlay);

      const gap = Math.max(10, Math.min(16, Math.floor(availW / 60)));

      const cardW = Math.floor((availW - gap * (cfg.cols - 1)) / cfg.cols);
      const cardH = Math.floor((availH - gap * (cfg.rows - 1)) / cfg.rows);

      const card = Math.max(56, Math.min(155, Math.min(cardW, cardH)));
      setLayout({ card, gap });
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [cfg.cols, cfg.rows, result]);

  // Timer
  useEffect(() => {
    if (!running) return;

    const id = setInterval(() => {
      setTime((t) => {
        if (!timed) return t + 1;
        if (t <= 1) {
          setRunning(false);
          setResult({ type: "lose", title: "Time’s up!", subtitle: "Try again — you’re warming up." });
          sfxRef.current.lose();
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [running, timed]);

  // rebuild on level/mode change
  useEffect(() => {
    const fresh = makeDeck(cfg);
    setDeck(fresh.deck);
    setFirst(null);
    setSecond(null);
    lockRef.current = false;

    setMoves(0);
    setMatches(0);
    setCombo(0);
    setGhost({ map: {}, until: 0 });

    setTime(timed ? cfg.timeLimit : 0);
    setRunning(true);
    setResult(null);

    setBest(Number(localStorage.getItem(`mm_best_${levelKey}`) || 0));
  }, [cfg, timed, levelKey]);

  // win
  useEffect(() => {
    if (matches !== pairs) return;

    setRunning(false);
    setResult({
      type: "win",
      title: "All matched! ✨",
      subtitle: combo >= 3 ? "Combo brain. 🔥" : "Clean and focused.",
    });
    sfxRef.current.win();

    if (timed) {
      setBest((prev) => {
        const used = cfg.timeLimit - time;
        const next = prev === 0 ? used : Math.min(prev, used);
        localStorage.setItem(bestKey, String(next));
        return next;
      });
    }
  }, [matches, pairs]); // eslint-disable-line react-hooks/exhaustive-deps

  // toast helper
  function showToast(msg, ms = 850) {
    setToast(msg);
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(""), ms);
  }

  // ghost expiry
  useEffect(() => {
    if (!ghost.until) return;
    const delay = Math.max(10, ghost.until - Date.now());
    const id = setTimeout(() => setGhost({ map: {}, until: 0 }), delay);
    return () => clearTimeout(id);
  }, [ghost]);

  function pulseCards(indices) {
    indices.forEach((idx) => {
      const el = document.querySelector(`[data-i="${idx}"]`);
      if (!el) return;
      el.classList.add(styles.pulse);
      setTimeout(() => el.classList.remove(styles.pulse), 360);
    });
  }

  function flip(i) {
    if (lockRef.current || !running) return;
    const c = deck[i];
    if (!c || c.flipped || c.matched) return;
    if (first !== null && i === first) return;

    const next = deck.slice();
    next[i] = { ...c, flipped: true };
    setDeck(next);
    sfxRef.current.flip();

    if (first === null) {
      setFirst(i);
      return;
    }

    if (second === null) {
      setSecond(i);
      setMoves((m) => m + 1);
      lockRef.current = true;

      setTimeout(() => {
        const a = next[first];
        const b = next[i];

        if (a && b && a.v === b.v) {
          next[first] = { ...a, matched: true };
          next[i] = { ...b, matched: true };
          setDeck(next);

          setMatches((x) => x + 1);
          setCombo((c0) => c0 + 1);

          sfxRef.current.match();
          pulseCards([first, i]);
          if (combo >= 1) showToast(`Combo ×${combo + 1}!`, 650);
        } else {
          setGhost({ map: { [first]: a?.v, [i]: b?.v }, until: Date.now() + 520 });
          next[first] = { ...a, flipped: false };
          next[i] = { ...b, flipped: false };
          setDeck(next);

          setCombo(0);
          sfxRef.current.miss();
          setShake(true);
          setTimeout(() => setShake(false), 240);
          showToast("Miss!", 600);
        }

        setFirst(null);
        setSecond(null);
        lockRef.current = false;
      }, 420);
    }
  }

  function restartSame() {
    sfxRef.current.unlock();
    sfxRef.current.ui();

    const fresh = makeDeck(cfg);
    setDeck(fresh.deck);
    setFirst(null);
    setSecond(null);
    lockRef.current = false;

    setMoves(0);
    setMatches(0);
    setCombo(0);
    setGhost({ map: {}, until: 0 });

    setTime(timed ? cfg.timeLimit : 0);
    setRunning(true);
    setResult(null);
  }

  function toggleSound() {
    sfxRef.current.unlock();
    sfxRef.current.ui();
    setSoundOn((v) => !v);
  }

  function toggleTimed() {
    sfxRef.current.unlock();
    sfxRef.current.ui();
    setTimed((v) => !v);
  }

  function togglePause() {
    sfxRef.current.unlock();
    sfxRef.current.ui();
    setRunning((r) => !r);
  }

  const bestLabel = timed ? (best ? `${best}s` : "—") : "—";
  const timeLabel = timed ? `${time}s` : `${time}s`;

  return (
    <div className={styles.wrap}>
      {/* CLEAN TOP NAV (restored) */}
      <header className={styles.top}>
        {/* <div className={styles.brand}>
          <div className={styles.title}>Neon Memory</div>
          <div className={styles.sub}>Pattern recall • Focus • Visual memory</div>
        </div> */}

        <div className={styles.stats}>
          <span className={styles.chip}>🧭 {fmtLevel(levelKey, cfg)}</span>
          <span className={styles.chip}>🧩 {matches}/{pairs}</span>
          <span className={styles.chip}>🌀 {moves}</span>
          <span className={styles.chip}>⏱ {timed ? "Left" : "Time"}: {timeLabel}</span>
          <span className={styles.chip}>🏆 {bestLabel}</span>
          {combo >= 2 && <span className={`${styles.chip} ${styles.combo}`}>🔥 ×{combo}</span>}
        </div>

        <div className={styles.actions}>
          <button className={styles.btn} onClick={toggleSound}>
            {soundOn ? "🔊 Sound" : "🔇 Sound"}
          </button>

          <select
            className={styles.select}
            value={levelKey}
            onChange={(e) => { sfxRef.current.unlock(); sfxRef.current.ui(); setLevelKey(e.target.value); }}
            title="Level"
          >
            <option value="4x4">Rookie — 4×4</option>
            <option value="5x4">Pro — 5×4</option>
          </select>

          <button className={styles.btn} onClick={toggleTimed}>
            {timed ? "⏱ Timed" : "∞ Free"}
          </button>

          <button className={styles.btn} onClick={togglePause}>
            {running ? "Pause" : "Resume"}
          </button>

          <button className={styles.btn} onClick={restartSame}>Restart</button>

          {/* {onExit && (
            <button className={`${styles.btn} ${styles.iconBtn}`} onClick={onExit} aria-label="Exit">
              ✕
            </button>
          )} */}
        </div>
      </header>

      {/* STAGE */}
      <main className={styles.stage} ref={stageRef}>
        <div
          className={`${styles.grid} ${shake ? styles.shake : ""}`}
          style={{
            gridTemplateColumns: `repeat(${cfg.cols}, ${layout.card}px)`,
            gridAutoRows: `${layout.card}px`,
            gap: `${layout.gap}px`,
          }}
          aria-label="Memory grid"
        >
          {deck.map((c, i) => {
            const isUp = c.flipped || c.matched;
            const ghostIcon = (!isUp && ghost.map && ghost.map[i]) ? ghost.map[i] : null;

            return (
              <button
                key={c.id}
                data-i={i}
                className={`${styles.card} ${isUp ? styles.up : ""} ${c.matched ? styles.matched : ""}`}
                onClick={() => flip(i)}
                aria-pressed={isUp}
              >
                <span className={`${styles.face} ${styles.front}`}>
                  {ghostIcon ? <span className={styles.ghost}>{ghostIcon}</span> : "?"}
                </span>
                <span className={`${styles.face} ${styles.back}`}>{c.v}</span>
              </button>
            );
          })}
        </div>

        {toast && <div className={styles.toast}>{toast}</div>}

        {/* GLASS RESULT OVERLAY (kept) */}
        {result && (
          <div className={`${styles.result} ${result.type === "win" ? styles.resultWin : styles.resultLose}`}>
            <div className={styles.resultHeader}>
              <div className={styles.resultTitle}>{result.title}</div>
              <div className={styles.resultSub}>{result.subtitle}</div>
            </div>

            <div className={styles.resultGrid}>
              <div className={styles.stat}>
                <div className={styles.k}>Level</div>
                <div className={styles.v}>{fmtLevel(levelKey, cfg)}</div>
              </div>
              <div className={styles.stat}>
                <div className={styles.k}>Moves</div>
                <div className={styles.v}>{moves}</div>
              </div>
              <div className={styles.stat}>
                <div className={styles.k}>Matches</div>
                <div className={styles.v}>{matches}/{pairs}</div>
              </div>
              <div className={styles.stat}>
                <div className={styles.k}>{timed ? "Time used" : "Time"}</div>
                <div className={styles.v}>{timed ? `${cfg.timeLimit - time}s` : `${time}s`}</div>
              </div>
              <div className={`${styles.stat} ${styles.statWide}`}>
                <div className={styles.k}>Best</div>
                <div className={styles.v}>{bestLabel}</div>
              </div>
            </div>

            <div className={styles.resultActions}>
              <button className={`${styles.btn} ${styles.primary}`} onClick={restartSame}>Play again</button>
              <button className={styles.btn} onClick={() => setResult(null)}>Keep board</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
